// FILE: NewThreadDialogContainer.tsx
// Purpose: Mount the "New thread" dialog once for the whole chat route and
//          wire it to the store that entry points push requests into.
// Layer: Web components

import { useCallback, useMemo } from "react";

import type { ProjectId } from "@taskin-code/contracts";

import { useAppSettings } from "../appSettings";
import { useCreateThreadFromDialog } from "../hooks/useCreateThreadFromDialog";
import { useNewThreadDialogStore } from "../newThreadDialogStore";
import { useStore } from "../store";
import { NewThreadDialog, type NewThreadDialogSubmit } from "./NewThreadDialog";

export function NewThreadDialogContainer() {
  const isOpen = useNewThreadDialogStore((store) => store.isOpen);
  const request = useNewThreadDialogStore((store) => store.request);
  const closeNewThreadDialog = useNewThreadDialogStore((store) => store.closeNewThreadDialog);
  const projects = useStore((store) => store.projects);
  const threads = useStore((store) => store.threads);
  const { settings } = useAppSettings();

  /**
   * Which pull requests already have a thread, so the picker can mark them.
   *
   * Read from the threads already in the store rather than asked of the server: the link lives on
   * the thread, every thread this account may see is here, and one more round trip to decorate a
   * picker would be a poor trade.
   */
  const linkedPullRequestNumbersByProject = useMemo(() => {
    const byProject = new Map<ProjectId, Set<number>>();
    for (const thread of threads) {
      const link = thread.linkedPullRequest;
      if (!link) continue;
      const existing = byProject.get(link.projectId);
      if (existing) existing.add(link.number);
      else byProject.set(link.projectId, new Set([link.number]));
    }
    return byProject as ReadonlyMap<ProjectId, ReadonlySet<number>>;
  }, [threads]);
  const createThread = useCreateThreadFromDialog();

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        closeNewThreadDialog();
      }
    },
    [closeNewThreadDialog],
  );

  const handleCreate = useCallback(
    async (input: NewThreadDialogSubmit) => {
      await createThread(input);
    },
    [createThread],
  );

  return (
    <NewThreadDialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      projects={projects}
      preferredProjectId={request.preferredProjectId}
      defaultEnvMode={settings.defaultThreadEnvMode}
      initialProvider={request.initialProvider}
      initialPullRequest={request.pullRequest}
      initialEnvironmentId={request.environmentId}
      initialQaOperation={request.qaOperation}
      initialPrompt={request.initialPrompt}
      linkedPullRequestNumbersByProject={linkedPullRequestNumbersByProject}
      onCreate={handleCreate}
    />
  );
}
